/*
 * Task service
 * Purpose: Centralize task API requests, normalization, and task update notifications for task UI consumers.
 * Implementation notes: The service hides route details, normalizes task data, and exposes backend-owned notification outcomes for composer saves.
 * Recent changes: Task create and notification-aware update calls carry sendEmail and expose a shared queue outcome type to task UI consumers.
 */

import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { request } from './dataService';
import {
  Task,
  TaskAssignee,
  TaskFilters,
  TaskMutationPayload,
  TaskPriority,
  TaskSourceType,
  TaskStatus,
  getTaskAssigneeLabel,
  getTaskSourceLabel,
} from '../models/Task';

export const TASKS_UPDATED_EVENT = 'rlpcrm:tasks-updated';

interface TaskApiRecord {
  id: number;
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority?: TaskPriority | null;
  dueDate?: string | null;
  accountId: number;
  accountName?: string | null;
  contactId?: number | null;
  contactName?: string | null;
  sourceNoteId?: number | null;
  sourceNoteTitle?: string | null;
  sourceType?: TaskSourceType | null;
  createdByUserId: number;
  createdByUserName?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  deletedAt?: string | null;
  deletedByUserId?: number | null;
  deletedByUserName?: string | null;
  assigneeIds?: string | number[] | null;
  assigneeNames?: string | string[] | null;
  notificationRequested?: boolean;
  notificationQueued?: number;
  notificationWarning?: string | null;
}

export interface TaskNotificationOutcome {
  requested: boolean;
  queued: number;
  warning?: string;
}

export interface TaskCreationResult {
  task: Task;
  notification: TaskNotificationOutcome;
}

export type TaskUpdateResult = TaskCreationResult;

const parseListValue = (value: string | number[] | string[] | null | undefined): string[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => `${item}`.trim()).filter(Boolean);
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeTask = (task: TaskApiRecord): Task => {
  const assigneeIds = parseListValue(task.assigneeIds).map((value) => Number(value)).filter((value) => Number.isFinite(value));
  const assigneeNames = parseListValue(task.assigneeNames);
  const assignees: TaskAssignee[] = assigneeNames.map((name, index) => ({
    id: assigneeIds[index] ?? 0,
    name,
  }));
  const sourceType = task.sourceType ?? (task.sourceNoteId ? 'Note' : 'Manual');

  return {
    id: task.id,
    title: task.title,
    description: task.description ?? '',
    status: task.status,
    priority: task.priority ?? 'Medium',
    dueDate: task.dueDate ?? null,
    accountId: task.accountId,
    accountName: task.accountName?.trim() || `Account #${task.accountId}`,
    contactId: task.contactId ?? null,
    contactName: task.contactName?.trim() || undefined,
    sourceNoteId: task.sourceNoteId ?? null,
    sourceNoteTitle: task.sourceNoteTitle?.trim() || undefined,
    sourceType,
    sourceLabel: getTaskSourceLabel(sourceType, task.sourceNoteId, task.sourceNoteTitle),
    createdByUserId: task.createdByUserId,
    createdByUserName: task.createdByUserName?.trim() || undefined,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    completedAt: task.completedAt ?? null,
    deletedAt: task.deletedAt ?? null,
    deletedByUserId: task.deletedByUserId ?? null,
    deletedByUserName: task.deletedByUserName?.trim() || undefined,
    assignees,
    assigneeIds,
    assigneeNames,
    assigneeName: getTaskAssigneeLabel(assignees),
  };
};

const buildTaskPayload = (payload: TaskMutationPayload) => ({
  ...payload,
  description: payload.description ?? '',
  dueDate: payload.dueDate ?? null,
  contactId: payload.contactId ?? null,
  sourceNoteId: payload.sourceNoteId ?? null,
  status: payload.status ?? 'Open',
  priority: payload.priority ?? 'Medium',
  sourceType: payload.sourceType ?? (payload.sourceNoteId ? 'Note' : 'Manual'),
  assigneeIds: payload.assigneeIds?.join(',') ?? '',
  sendEmail: payload.sendEmail ?? true,
});

const buildTaskPatchPayload = (payload: Partial<TaskMutationPayload>) => {
  const patchPayload: Record<string, unknown> = {
    title: payload.title ?? null,
    description: payload.description ?? null,
    accountId: payload.accountId ?? null,
    contactId: payload.contactId ?? null,
    sourceNoteId: payload.sourceNoteId ?? null,
    dueDate: payload.dueDate ?? null,
    status: payload.status ?? null,
    priority: payload.priority ?? null,
    assigneeIds: payload.assigneeIds?.join(',') ?? '',
    clearContact: payload.contactId === null,
    clearSourceNote: payload.sourceNoteId === null,
    clearDueDate: payload.dueDate === null,
    replaceAssignees: Array.isArray(payload.assigneeIds),
    sendEmail: payload.sendEmail ?? false,
  };

  return patchPayload;
};

export const publishTasksUpdated = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(TASKS_UPDATED_EVENT));
};

export const getTasks = async (filters: TaskFilters = {}): Promise<Task[]> => {
  const tasks = await request(API_ENDPOINTS.GET_TASKS, filters);
  return (Array.isArray(tasks) ? tasks : []).map(normalizeTask);
};

export const getTask = async (id: number): Promise<Task | null> => {
  const task = await request(API_ENDPOINTS.GET_TASK, { id });
  return task ? normalizeTask(task) : null;
};

export const getTasksForAccount = async (accountId: number): Promise<Task[]> => {
  const tasks = await request(API_ENDPOINTS.GET_ACCOUNT_TASKS, { id: accountId });
  return (Array.isArray(tasks) ? tasks : []).map(normalizeTask);
};

export const getTasksForContact = async (contactId: number): Promise<Task[]> => {
  const tasks = await request(API_ENDPOINTS.GET_CONTACT_TASKS, { id: contactId });
  return (Array.isArray(tasks) ? tasks : []).map(normalizeTask);
};

export const getTasksForNote = async (noteId: number): Promise<Task[]> => {
  const tasks = await request(API_ENDPOINTS.GET_NOTE_TASKS, { noteId });
  return (Array.isArray(tasks) ? tasks : []).map(normalizeTask);
};

export const getTaskCountForNote = async (noteId: number): Promise<number> => {
  const tasks = await getTasksForNote(noteId);
  return tasks.length;
};

export const createTask = async (payload: TaskMutationPayload): Promise<TaskCreationResult> => {
  const createdTask = await request(API_ENDPOINTS.POST_TASK, buildTaskPayload(payload));
  const normalizedTask = normalizeTask(createdTask);
  publishTasksUpdated();
  return {
    task: normalizedTask,
    notification: {
      requested: createdTask.notificationRequested === true,
      queued: Number(createdTask.notificationQueued ?? 0),
      ...(createdTask.notificationWarning?.trim()
        ? { warning: createdTask.notificationWarning.trim() }
        : {}),
    },
  };
};

const normalizeTaskMutationResult = (task: TaskApiRecord): TaskCreationResult => ({
  task: normalizeTask(task),
  notification: {
    requested: task.notificationRequested === true,
    queued: Number(task.notificationQueued ?? 0),
    ...(task.notificationWarning?.trim()
      ? { warning: task.notificationWarning.trim() }
      : {}),
  },
});

export const updateTaskWithNotification = async (
  id: number,
  payload: Partial<TaskMutationPayload>
): Promise<TaskUpdateResult> => {
  const updatedTask = await request(API_ENDPOINTS.PATCH_TASK, { id, ...buildTaskPatchPayload(payload) });
  const result = normalizeTaskMutationResult(updatedTask);
  publishTasksUpdated();
  return result;
};

export const updateTask = async (id: number, payload: Partial<TaskMutationPayload>): Promise<Task> => {
  const result = await updateTaskWithNotification(id, payload);
  return result.task;
};

export const updateTaskStatus = async (id: number, status: TaskStatus): Promise<Task> => {
  const updatedTask = await request(API_ENDPOINTS.PATCH_TASK_STATUS, { id, status });
  const normalizedTask = normalizeTask(updatedTask);
  publishTasksUpdated();
  return normalizedTask;
};

export const deleteTask = async (id: number): Promise<void> => {
  await request(API_ENDPOINTS.DELETE_TASK, { id });
  publishTasksUpdated();
};

export const rescheduleTask = async (id: number, dueDate: string | null): Promise<Task> => {
  return updateTask(id, { dueDate });
};
